/**
 * HemisphereLightComponent - ambient lighting from sky and ground.
 * Encapsulates a THREE.HemisphereLight with separate sky and ground colors.
 */
import * as THREE from 'three';

import { EngineClass } from '../../systems/ClassRegistry.js';
import { property } from '../../utils/serialization/decorator.js';

import { LightComponent } from './LightComponent.js';

import type { LightComponentOptions } from './LightComponent.js';


export interface HemisphereLightComponentOptions extends LightComponentOptions {
  /** Color of the light from the ground */
  groundColor?: THREE.ColorRepresentation;
}

/** Wraps a THREE.HemisphereLight.
 * Note: Use the inherited `color` property for the sky color (upper hemisphere).
 */
@EngineClass('HemisphereLightComponent')
export class HemisphereLightComponent extends LightComponent<THREE.HemisphereLight, HemisphereLightComponentOptions> {

  @property({ transient: true })
  get groundColor(): THREE.Color {
    return this.light?.groundColor ?? new THREE.Color(0xFFFFFF);
  }
  set groundColor(value: THREE.ColorRepresentation) {
    this.light?.groundColor.set(value);
  }

  protected override createLight(): THREE.HemisphereLight {
    return new THREE.HemisphereLight();
  }

  public override initialize(options?: HemisphereLightComponentOptions): void {
    super.initialize(options);
    this.groundColor = options?.groundColor ?? this.groundColor;
  }

  /** Hemisphere lights don't cast shadows. */
  protected override configureShadow(): this {
    // Hemisphere lights don't cast shadows, so this is a no-op
    return this;
  }

  protected override createLightHelper(): THREE.Object3D | null {
    if (this.light) {
      return new THREE.HemisphereLightHelper(this.light, 1 /** Size */);
    }
    return null;
  }

  //====================================================================================================
  // Editor Functions
  //====================================================================================================

  public override getEditorClassIcon(): string | null {
    return 'Icon_Hemisphere';
  }
}
